if (!customElements.get('article-toc')) {
  customElements.define(
    'article-toc',
    class ArticleToc extends HTMLElement {
      connectedCallback() {
        const content = document.getElementById(this.dataset.contentId);
        const navigation = this.querySelector('nav');
        const toggle = this.querySelector('.article-template__toc-toggle');

        if (!content || !navigation || !toggle) return;

        [...content.querySelectorAll('h2, h3')].forEach((heading) => {
          const images = [...heading.querySelectorAll('img')];

          if (images.length > 0) {
            const media = document.createElement('figure');
            media.className = 'article-template__moved-media';
            heading.before(media);
            images.forEach((image) => media.appendChild(image));
          }

          heading.querySelectorAll('br').forEach((lineBreak) => lineBreak.remove());
        });

        const headings = [...content.querySelectorAll('h2')].filter((heading) => heading.textContent.trim());
        if (headings.length < 2) return;

        const usedIds = new Set();
        const links = headings.map((heading, index) => {
          const baseId =
            heading.id ||
            heading.textContent
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-') ||
            `section-${index + 1}`;
          let uniqueId = baseId;
          let suffix = 2;

          while (usedIds.has(uniqueId) || (document.getElementById(uniqueId) && document.getElementById(uniqueId) !== heading)) {
            uniqueId = `${baseId}-${suffix}`;
            suffix += 1;
          }

          usedIds.add(uniqueId);
          heading.id = uniqueId;
          heading.classList.add('article-template__anchor-heading');

          const link = document.createElement('a');
          link.href = `#${uniqueId}`;
          link.textContent = heading.textContent.trim();
          link.className = 'article-template__toc-link';
          navigation.appendChild(link);
          return link;
        });

        this.hidden = false;
        this.toggle = toggle;
        this.navigation = navigation;
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.handleToggle = () => this.setExpanded(this.toggle.getAttribute('aria-expanded') !== 'true');
        this.handleBreakpointChange = (event) => this.setExpanded(event.matches);
        this.headings = headings;
        this.links = links;
        this.handleLinkClick = (event) => {
          this.pendingTargetId = event.currentTarget.hash.slice(1);
          this.setActiveLink(event.currentTarget);
        };
        this.handleScroll = () => {
          window.clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = window.setTimeout(() => {
            this.pendingTargetId = null;
            this.requestActiveLinkUpdate();
          }, 120);

          if (!this.pendingTargetId) this.requestActiveLinkUpdate();
        };
        this.handleResize = () => this.requestActiveLinkUpdate();
        this.handleHashChange = () => {
          const hashLink = this.links.find((link) => link.hash === window.location.hash);
          if (hashLink) this.setActiveLink(hashLink);
        };
        this.toggle.addEventListener('click', this.handleToggle);
        this.desktopQuery.addEventListener('change', this.handleBreakpointChange);
        links.forEach((link) => link.addEventListener('click', this.handleLinkClick));
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('hashchange', this.handleHashChange);
        this.setExpanded(this.desktopQuery.matches);
        this.handleHashChange();
        this.requestActiveLinkUpdate();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.handleToggle);
        this.desktopQuery?.removeEventListener('change', this.handleBreakpointChange);
        this.links?.forEach((link) => link.removeEventListener('click', this.handleLinkClick));
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('hashchange', this.handleHashChange);
        window.clearTimeout(this.scrollEndTimer);
        if (this.activeLinkFrame) window.cancelAnimationFrame(this.activeLinkFrame);
      }

      setExpanded(expanded) {
        this.toggle.setAttribute('aria-expanded', String(expanded));
        this.navigation.hidden = !expanded;
      }

      requestActiveLinkUpdate() {
        if (this.activeLinkFrame) return;

        this.activeLinkFrame = window.requestAnimationFrame(() => {
          this.activeLinkFrame = null;
          this.updateActiveLink();
        });
      }

      updateActiveLink() {
        const activationLine = Math.min(160, window.innerHeight * 0.3);
        let activeIndex = 0;

        this.headings.forEach((heading, index) => {
          if (heading.getBoundingClientRect().top <= activationLine) activeIndex = index;
        });

        this.setActiveLink(this.links[activeIndex]);
      }

      setActiveLink(activeLink) {
        this.links.forEach((link) => {
          if (link === activeLink) {
            link.setAttribute('aria-current', 'location');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}

if (!customElements.get('article-reading-progress')) {
  customElements.define(
    'article-reading-progress',
    class ArticleReadingProgress extends HTMLElement {
      connectedCallback() {
        this.content = document.getElementById(this.dataset.contentId);
        this.indicator = this.querySelector('span');
        if (!this.content || !this.indicator) return;

        this.update = this.update.bind(this);
        this.requestUpdate = () => requestAnimationFrame(this.update);
        this.update();
        window.addEventListener('scroll', this.requestUpdate, { passive: true });
        window.addEventListener('resize', this.requestUpdate);
      }

      disconnectedCallback() {
        if (!this.requestUpdate) return;
        window.removeEventListener('scroll', this.requestUpdate);
        window.removeEventListener('resize', this.requestUpdate);
      }

      update() {
        const contentTop = this.content.getBoundingClientRect().top + window.scrollY;
        const readableDistance = Math.max(this.content.offsetHeight - window.innerHeight * 0.5, 1);
        const progress = Math.min(Math.max((window.scrollY - contentTop) / readableDistance, 0), 1);
        this.indicator.style.transform = `scaleX(${progress})`;
      }
    }
  );
}
